/**
 * X3 RAIL (Composer Spec 3, "pre-filled before the owner opens the thread").
 *
 * The merchandiser's picks, pure and deterministic. The rail shows at most
 * three products and two media assets for a thread, each with a one-line why,
 * and nothing it could not insert: a pick is a suggestion the owner may press,
 * never text the customer sees until a person approves a draft that holds it.
 *
 * What this file will not do, by construction:
 *  - It reads no wealth signal. VIP status and any inferred spending power are
 *    not parameters, so they cannot move a pick (CLAUDE.md section 3: VIP
 *    changes service level and routing, never a number). The only money input
 *    is a budget the customer STATED, as a ceiling.
 *  - It never picks above that ceiling, never picks a product whose catalog
 *    verdict is not insertable, and never picks a product with no price.
 *  - It writes no figure into the why line. A price in prose is a customer
 *    facing risk the verifier would have to catch; the owner sees the price on
 *    the product card, from the record.
 *  - Precision beats recall (section 0): a candidate with no tag in common
 *    with the conversation and no proven history is not shown. An empty rail
 *    says so rather than filling the space.
 *
 * Insertability is decided by the catalog (X2's `catalogInsertable`,
 * `mediaInsertable`) and handed in as each candidate's verdict, so this module
 * has no second definition of "sold" or "stale" to drift from the real one.
 * Model ranking for ties (the design allows it through the gateway) is NOT
 * built: product ties break by proven conversion history, media ties by
 * engagement (TA-1259), then by id, deterministically.
 */

/** At most this many product picks per draft set (design X3). AWAITING RATIFICATION. */
export const MAX_PRODUCT_PICKS = 3;
/** At most this many media picks per draft set (design X3). AWAITING RATIFICATION. */
export const MAX_MEDIA_PICKS = 2;
/**
 * Fewest times a product must have been shown before its conversion rate may
 * break a tie. Below it the rate is noise (B13). AWAITING RATIFICATION.
 */
export const RAIL_MIN_CONVERSION_SAMPLE = 5;

/** The catalog's verdict on one record: insertable, or the one reason it is not. */
export type InsertVerdict = { readonly ok: true } | { readonly ok: false; readonly reason: string };

export interface ProductCandidate {
  readonly id: string;
  /** The record version the verdict was computed on; the insert pins it. */
  readonly version: number;
  /** Integer cents from the record, or null for a record with no price. */
  readonly priceCents: number | null;
  /** Lowercase-comparable descriptors: metal, stone, cut, style. */
  readonly tags: readonly string[];
  readonly insertable: InsertVerdict;
}

export interface MediaCandidate {
  readonly id: string;
  readonly tags: readonly string[];
  readonly insertable: InsertVerdict;
}

/** A stated budget ceiling in integer cents, or null when the customer stated none. */
export type BudgetSignal = { readonly maxCents: number } | null;

export interface ConversionStat {
  readonly shown: number;
  readonly converted: number;
}

export interface Pick {
  readonly id: string;
  readonly version: number | null;
  readonly rank: number;
  readonly why: string;
}

export interface Excluded {
  readonly id: string;
  readonly reason: string;
}

export interface PickResult {
  readonly picks: readonly Pick[];
  readonly excluded: readonly Excluded[];
}

const norm = (tags: readonly string[]): Set<string> => new Set(tags.map((t) => t.trim().toLowerCase()).filter((t) => t !== ""));

function overlap(candidate: readonly string[], conversation: ReadonlySet<string>): string[] {
  return [...norm(candidate)].filter((t) => conversation.has(t)).sort();
}

function provenRate(stat: ConversionStat | undefined): number {
  if (!stat || !(stat.shown >= RAIL_MIN_CONVERSION_SAMPLE) || stat.shown <= 0) return 0;
  return stat.converted / stat.shown;
}

export function pickProducts(input: {
  readonly candidates: readonly ProductCandidate[];
  readonly budgetSignal: BudgetSignal;
  readonly conversationTags: readonly string[];
  readonly conversionStats: Readonly<Record<string, ConversionStat>>;
}): PickResult {
  const conversation = norm(input.conversationTags);
  const excluded: Excluded[] = [];
  const scored: { c: ProductCandidate; matched: string[]; rate: number }[] = [];

  for (const c of input.candidates) {
    if (!c.insertable.ok) {
      excluded.push({ id: c.id, reason: c.insertable.reason });
      continue;
    }
    if (c.priceCents === null || !Number.isInteger(c.priceCents) || c.priceCents <= 0) {
      excluded.push({ id: c.id, reason: "no_price" });
      continue;
    }
    if (input.budgetSignal !== null && c.priceCents > input.budgetSignal.maxCents) {
      excluded.push({ id: c.id, reason: "over_budget" });
      continue;
    }
    const matched = overlap(c.tags, conversation);
    if (matched.length === 0) {
      excluded.push({ id: c.id, reason: "no_signal" });
      continue;
    }
    scored.push({ c, matched, rate: provenRate(input.conversionStats[c.id]) });
  }

  scored.sort((a, b) => b.matched.length - a.matched.length || b.rate - a.rate || (a.c.id < b.c.id ? -1 : a.c.id > b.c.id ? 1 : 0));

  const top = scored.slice(0, MAX_PRODUCT_PICKS);
  for (const s of scored.slice(MAX_PRODUCT_PICKS)) excluded.push({ id: s.c.id, reason: "over_cap" });

  const picks = top.map((s, i): Pick => {
    const budget = input.budgetSignal === null ? "no budget stated" : "within the stated budget";
    const history = s.rate > 0 ? "; has led to a sale before" : "";
    return {
      id: s.c.id,
      version: s.c.version,
      rank: i + 1,
      why: `matches ${s.matched.join(", ")}; ${budget}${history}`,
    };
  });
  return { picks, excluded };
}

export function pickMedia(input: {
  readonly candidates: readonly MediaCandidate[];
  readonly conversationTags: readonly string[];
  /** Asset ids already sent to this contact, per `rail_inserts`. */
  readonly alreadySentAssetIds: readonly string[];
  /**
   * TA-1259 (Inbox Composer 4: "prefers assets with proven engagement"): one
   * number per asset id. A tiebreak only, after tag overlap and before id, so
   * it can reorder assets that fit the conversation equally and never puts a
   * worse fit ahead or rescues an asset with no tag in common. An asset absent
   * from the map, or with a non-finite value, ranks as zero.
   */
  readonly engagement?: Readonly<Record<string, number>>;
}): PickResult {
  const conversation = norm(input.conversationTags);
  const sent = new Set(input.alreadySentAssetIds);
  const excluded: Excluded[] = [];
  const scored: { c: MediaCandidate; matched: string[]; engagement: number }[] = [];
  const engagementOf = (id: string): number => {
    const v = input.engagement !== undefined && Object.hasOwn(input.engagement, id) ? input.engagement[id] : undefined;
    return typeof v === "number" && Number.isFinite(v) ? v : 0;
  };

  for (const c of input.candidates) {
    if (!c.insertable.ok) {
      excluded.push({ id: c.id, reason: c.insertable.reason });
      continue;
    }
    if (sent.has(c.id)) {
      excluded.push({ id: c.id, reason: "already_sent" });
      continue;
    }
    const matched = overlap(c.tags, conversation);
    if (matched.length === 0) {
      excluded.push({ id: c.id, reason: "no_signal" });
      continue;
    }
    scored.push({ c, matched, engagement: engagementOf(c.id) });
  }

  scored.sort(
    (a, b) =>
      b.matched.length - a.matched.length ||
      (b.engagement > a.engagement ? 1 : b.engagement < a.engagement ? -1 : 0) ||
      (a.c.id < b.c.id ? -1 : a.c.id > b.c.id ? 1 : 0),
  );
  for (const s of scored.slice(MAX_MEDIA_PICKS)) excluded.push({ id: s.c.id, reason: "over_cap" });
  const picks = scored.slice(0, MAX_MEDIA_PICKS).map(
    (s, i): Pick => ({ id: s.c.id, version: null, rank: i + 1, why: `matches ${s.matched.join(", ")}` }),
  );
  return { picks, excluded };
}
